import React from 'react';
import {
  Box, Typography, LinearProgress, IconButton, Chip,
  Collapse, Paper, Tooltip, alpha
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useJobs } from '../contexts/JobContext';

export default function JobStatusBar() {
  const { jobs, dismissJob } = useJobs();

  const jobList = Object.values(jobs).filter(job => !job.hidden);
  const active = jobList.filter(j => j.status === 'pending' || j.status === 'processing');
  const done   = jobList.filter(j => j.status === 'completed' || j.status === 'failed');

  if (jobList.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1400,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        maxWidth: 360,
        width: '100%',
      }}
    >
      {/* Active jobs */}
      {active.map(job => (
        <Paper
          key={job.job_id}
          elevation={6}
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'primary.main',
            bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <AutoAwesomeIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
            <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }} noWrap>
              {job.label || 'AI Processing…'}
            </Typography>
            <Chip label="Running" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
          </Box>
          <LinearProgress variant="indeterminate" sx={{ borderRadius: 1, height: 3 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            You can navigate freely — this runs in the background.
          </Typography>
        </Paper>
      ))}

      {/* Completed / failed jobs */}
      {done.map(job => (
        <Paper
          key={job.job_id}
          elevation={4}
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: job.status === 'completed' ? 'success.main' : 'error.main',
            bgcolor: theme => alpha(
              job.status === 'completed' ? theme.palette.success.main : theme.palette.error.main,
              0.07
            ),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {job.status === 'completed'
              ? <CheckCircleIcon fontSize="small" color="success" sx={{ flexShrink: 0 }} />
              : <ErrorIcon fontSize="small" color="error" sx={{ flexShrink: 0 }} />
            }
            <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }} noWrap>
              {job.label || 'AI Job'}
            </Typography>
            <Typography variant="caption" color={job.status === 'completed' ? 'success.main' : 'error.main'} sx={{ flexShrink: 0 }}>
              {job.status === 'completed' ? 'Done' : 'Failed'}
            </Typography>
            <Tooltip title="Dismiss">
              <IconButton size="small" onClick={() => dismissJob(job.job_id)} sx={{ ml: 0.5 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {job.status === 'failed' && job.error_message && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }} noWrap>
              {job.error_message}
            </Typography>
          )}
        </Paper>
      ))}
    </Box>
  );
}
